/**
 * @typedef {object} LabelText
 * @prop {string} text label text
 * @prop {string} classes CSS classes
 */

const template = /* html */`

<style>
fieldset {
  margin: 0;
  border: 0;
  padding: 0;

}

fieldset legend.has-legend {
  margin-bottom: var( --sm-margin );
}

fieldset div {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto;
}

fieldset.column div {
  grid-template-columns: 1fr;
  grid-template-rows: auto;
}
</style>

<fieldset>
  <legend class="label bold radio-group-legend-2"><slot name="legend"></slot></legend>
  <div>
    <slot name="options"></slot>
  </div>
</fieldset>
`;

/**
 * RadioGroup Web component that can contain one to n-amount of radio-inputs.
 * The component is a form associated element so it can be used as a part of typical HTMLFormElement.
 * Form's reset, disabled and other typical events are supported. Data is passed in a FormData when a
 * form is submitted, like a any other form element.
 *
 * @example
 * To listen changes of element:
 *
 * document.querySelector('radio-group').addEventListener('change', (event) => { console.log(event.details)});
 */
class RadioGroup extends HTMLElement {

  // Identify the element as a form-associated custom element
  static formAssociated = true;

  constructor(){
    super();

    //Associates element with a HTMLFormElement functionalities, like form reset that kind of stuff.
    //Safari browser has not yeat implemented functionality so, check that function exist before calling it.
    if( typeof this.attachInternals === 'function' ) {
      this.internals_ = this.attachInternals();
    }

    const templateElement = document.createElement('template');
    templateElement.innerHTML = template;

    const shadowRoot = this.attachShadow({mode: 'open'});
    shadowRoot.appendChild(templateElement.content.cloneNode(true));

    //Pass classes from RadioGroup to fieldset
    const fieldset = shadowRoot.querySelector('fieldset');

    this.classList.forEach( token => {
      fieldset.classList.add( token );
    });
  }

  /**
   * Returns currently checked options value or falsy value if nothing is found
   * @return {string?} value
   */
  get value() {
    const selectedElement = this._radioElements?.find( element => element.input.checked );
    return selectedElement?.input.value;
  }

  /**
   * Set an first radio group option checked with a passed `value`, if there is a option with the value passed.
   * If no option found with passed `value` whole thing is just ingored.
   * @param {string} value radio group option's value
   */
  set value(value) {
    const radioElement = this._radioElements.find( element => {
      return element.input.value === String(value);
    });

    if( radioElement ) {
      this._uncheckRadioElements();
      radioElement.label.classList.add('checked');
      radioElement.checked = true;
    }
  }

  /**
   * Can be used to set visible text of RadioGroup element.
   * @param {string} value value of a radio group label
   * @param {LabelText[]} texts texts shown as a span elements in radio group label
   */
  setLabelText( value, texts ) {

    let textArray = !Array.isArray( texts ) ? [texts] : texts;

    //Create span elements from strings
    const spanElements = textArray.map( text => {
      const spanElement = document.createElement('span');
      spanElement.innerHTML = text.text;//Handles HTML entities like nbsp;
      spanElement.classList.add(...text.classes);
      return spanElement;
    });

    const radioElement = this._radioElements.find( element => {
      return element.input.value === String(value);
    });

    if( radioElement ) {
      //Remove old span elements, it contains input and ::after Nodes so we only want to remove span element here..
      radioElement.label.querySelectorAll('span').forEach( spanElement => {
        radioElement.label.removeChild( spanElement );
      });

      //Add new span elements to label
      spanElements.forEach(spanElement => {
        radioElement.label.appendChild( spanElement );
      });
    } else {
      console.warn('No label found from a RadioGroup element with a given value', value)
    }
  }

  /**
   * Set disabled attribute
   * @param {boolean} value is radio group disabled
   */
  set disabled( value ) {
    if( value ) {
      this.setAttribute( 'disabled', true );
    } else {
      this.removeAttribute('disabled')
    }
  }

  /**
   * Return radio group disabled attribute value
   * @return {boolean} true if disabled false if not
   */
  get disabled() {
    const disabled = this.getAttribute('disabled');
    return !(typeof disabled === 'object' && disabled === null);
  }

  /**
   * Web component licycle callback, called when a element is connected to DOM
   */
  connectedCallback() {

    if( this.querySelector('.radio-group-legend')?.innerText.length > 0 && this.internals_) {
      this.internals_?.shadowRoot?.querySelector('fieldset legend')?.classList?.add('has-legend')
    }

    const elements = Array.from( this.querySelectorAll('label') );

    this._radioElements = elements.map((element) => {
      element.addEventListener('change', this._onRadioElementClicked.bind(this) );

      const input = element.querySelector('input[type="radio"]');
      element.classList.toggle('checked', input.checked);
      element.setAttribute('tabindex', 0 );
      input.disabled = this.disabled;

      const data = {
        label: element,
        input
      }

      //When a form reset button is called, formResetCallback() method is called to return orginal value.
      if( input.checked ) {
        this._orginalCheckedElement = data;
      }

      return data;
    });
  }

  /**
   * Lifecycle callback. This is called when 'disabled' attribute of this element or achestor <fieldset> is updated.
   * @param {boolean} disabled is element disabled or not
   */
  formDisabledCallback(disabled) {
    this._radioElements.forEach(element => {
      element.disabled = disabled;
    });
  }

  /**
   * Lifecycle callback. This is called when the owner form is reset.
   */
  formResetCallback() {

    this._uncheckRadioElements();

    if( this._orginalCheckedElement ) {
      this._orginalCheckedElement.label.classList.toggle('checked', true);
      this._orginalCheckedElement.input = true;
    }
  }

  /**
   * Lifecycle callback. Called when Browser want to restore element state.. not sure what to do with this.
   */
  formStateRestoreCallback() {}

  /**
   * Called when a element is removed from DOM
   */
  disconnectedCallback() {
    this._radioElements.forEach((radio) => {
      radio.label.removeEventListener('change', this._onRadioElementClicked);
    });
  }

  /**
   * Called on when radio element is clicked
   * @param {Event} event click event object
   */
  _onRadioElementClicked = (event) => {

    //This event don't need to futher..
    event.stopPropagation(true);

    const element = event.target;

    if( element && !this.disabled) {

      this._uncheckRadioElements();

      element.parentElement.classList.add('checked');
      element.setAttribute('checked', '');
      element.checked = true;

      //Due event retargeting, way how Web components events are encapsulated we need to dispatch stuff in a CustomEvent
      this.dispatchEvent( new CustomEvent('change', {
        bubbles: true,//The bubbles property specifies whether the event should be propagated upward to the parent element
        composed: true,//The composed property specifies whether an event should bubble across from the shadow DOM,
        detail: element.value
      }));
    }
  }

  /**
   * Uncheck all radio elements
   */
  _uncheckRadioElements() {
    this._radioElements.forEach(( radioElement ) => {
      radioElement.label.classList.remove('checked');
      radioElement.input.removeAttribute('checked');
      radioElement.input.checked = false;
    });
  }
}

export default RadioGroup;

